import { Component, HostListener, Inject, Input } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { filter, take } from 'rxjs/operators';
import { MODAL_EVENT_OPEN, MODAL_EVENT_CLOSE, DwMenuService, DwMenuServiceState, DwSecurityUserService, DwAuthenticationType, DwSecurityStateService, DevwareUiMetaDataRegistrationService, DwEventService, DwOrmDataService, DwMetaDataService, DwMetaDataServiceToken, areEqual, DwLoggingService, DwAppInitService } from '@devwareapps/devware-cap';
import { environment } from 'src/environments/environment';
import { AviatorUser } from '../../features/shared/models/aviator-user.model';
import { DwThemeManagerService } from '@devwareapps/devware-cap/devware-admin/themes-admin/services/dw-theme-manger.service';
import { SchoolBrandingService } from '../../features/schools/services/school-branding.service';
import { AdminManagerService } from '../../features/admin/services/admin-manager.service';
import { SchoolSelectionModalActionService } from '../../features/admin/actions/school-select-modal-action.service';
import { AppPermissions } from '../../meta-data/app-permissions.enum';
import { AppMetaData, AppMetaDataLookups } from '../../meta-data/app-meta-data.service';
import { delay } from 'rxjs/operators';
import { AppDevwareConfigProviderService } from '../../app-devware-config-provider.service';
import { HelpScoutBeaconService } from '../../features/help-scout-beacon/services/help-scout-beacon.service';
import { SchoolRepositoryService } from '../../features/schools/services/school-repository.service';
import { SchoolFeatures } from '../../features/schools/models/school-features.model';
import { StripeDiagnosticsService } from '../../features/payments/services/stripe-diagnostics.service';
import { DelayedCheckoutRecoveryService } from '../../features/payments/services/delayed-checkout-recovery.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent {
  showLayout: boolean = true;

  showSplashScreen: boolean = true;
  environmentMessage: string;
  scrolled = false;

  courseSideBarClass = '';

  themeClasses: string;
  private pendingThemeClasses: string;
  private themeUpdateQueued = false;

  showSideLogo: boolean = true;

  currentUser: AviatorUser;
  flightSchoolName: string = 'AviatorPro';
  logoutComplete: boolean;
  flightSchoolOverridden: boolean = true;

  @Input() registrationItems = false;
  @Input() registrationActions = false;
  schoolFeatures: SchoolFeatures;

  initialThemeComplete = false;
  lastSecurityState: any;
  isOffline = false;

  constructor(
    private router: Router,
    private menuService: DwMenuService,
    private dwSecurityUserService: DwSecurityUserService,
    private schoolBrandingService: SchoolBrandingService,
    private adminManagerService: AdminManagerService,
    private schoolSelectModalActionService: SchoolSelectionModalActionService,
    private dwSecurityStateService: DwSecurityStateService,
    private devwareUiMetaDataRegistrationService: DevwareUiMetaDataRegistrationService,
    private dwEventService: DwEventService,
    private schoolRepository: SchoolRepositoryService,
    @Inject(DwMetaDataServiceToken) private dwMetaDataService: DwMetaDataService,
    private helpScoutBeaconService: HelpScoutBeaconService,
    private dwLoggingService: DwLoggingService,
    private stripeDiagnosticsService: StripeDiagnosticsService,
    private delayedCheckoutRecoveryService: DelayedCheckoutRecoveryService,
    private dwAppInitService: DwAppInitService,
    private titleService: Title

  ) {

    if (this.registrationItems) {
      //console.log('Register Items');
      this.devwareUiMetaDataRegistrationService.registerItems();
    }

    if (this.registrationActions) {
      //console.log('Register Actions');
      this.devwareUiMetaDataRegistrationService.registerActions();
    }
  }

  // Close the mobile drawer when the user taps anywhere outside of it
  // (the dimmed backdrop or any other non-sidebar area). The hamburger
  // toggler is excluded so its own toggle isn't immediately undone.
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!document.body.classList.contains('sidebar-show')) {
      return;
    }

    const target = event.target as HTMLElement | null;
    if (!target) {
      return;
    }

    if (target.closest('.sidebar, .sidebar-nav, .replacement-nav')) {
      return;
    }

    if (target.closest('.navbar-toggler, [appSidebarToggler]')) {
      return;
    }

    document.body.classList.remove('sidebar-show');
  }

  ngOnInit(): void {
    const environmentConfigItem = AppDevwareConfigProviderService.getEnvironmentConfigItem();
    this.environmentMessage = environmentConfigItem.envMessage;
    this.titleService.setTitle(AppDevwareConfigProviderService.getEnvironmentBaseTitle());
    this.syncEnvironmentTitle();
    this.stripeDiagnosticsService.installGlobalHandlers();
    this.delayedCheckoutRecoveryService.start();

    this.initFlightSchoolFeatures();

    const bodyClasses = document.body.classList;

    this.dwSecurityUserService.securityContextChanged().subscribe(change => {
      const state = this.dwSecurityStateService.securityState;

      if (state.isAuthenticating || state.isRefreshing) {
        //console.log('Show Splash screen');
        this.showSplashScreen = true;
        return;
      }

      if (this.shouldRedirectToLoginAfterInactivityLogout(state)) {
        this.router.navigateByUrl('/login');
        return;
      }

      //console.log('Security Context Changed', state);

      let forceSetTheme = false;
      if (this.lastSecurityState && this.lastSecurityState.isAuthenticated != state.isAuthenticated) {
        // authentication state changed
        if (state.isAuthenticated) {

          // console.log('Reset App Setup Complete');
          forceSetTheme = true;

          setTimeout(() => {
            this.appSetupComplete = false;
            this.showSplashScreen = true;
            //console.log('Show splash screen');
            this.initialThemeComplete = false;

            // Refreshes the themese and remove the app-setup-complete class so the splash screen can show
            this.updateThemes(this.themeClasses);
          });
        }
      }

      this.lastSecurityState = state;

      this.loadFlightSchoolFeatures(forceSetTheme);
      //this.setTheme(this.menuService.menuState);
    });

    this.menuService.menuStateObservable
      .subscribe(menuState => {
        this.setTheme(menuState);
      });

    this.dwEventService.getEvent(DwOrmDataService.ORM_EVENT_TOPICS.ItemChangedTopic(AppMetaData.FlightSchool.ItemDetail.itemName))
      .pipe(delay(300))
      .subscribe((flightSchool) => {
        // console.log('Flight School Changed, updating name', flightSchool);
        // this.currentUser = this.adminManagerService.getAviatorUser(this.dwSecurityUserService.securityState?.securityContext?.ApplicationUser);
        // this.flightSchoolName = this.currentUser?.FlightSchoolName;
        this.loadFlightSchoolFeatures();
      });

    this.setTheme(this.menuService.menuState);

    this.helpScoutBeaconService.setupBeaconEvents();

    this.setupOfflineModes();
  }

  setupOfflineModes() {
    this.dwLoggingService.getOfflineState()
      .subscribe((isOffline) => {

        this.isOffline = isOffline;
      });

    this.isOffline = this.dwLoggingService.isOffline();

  }

  private syncEnvironmentTitle() {
    this.dwAppInitService.getAppInitResult()
      .pipe(take(1))
      .subscribe(appInitResult => {
        if (!appInitResult?.EnvironmentInfo) {
          return;
        }

        this.titleService.setTitle(AppDevwareConfigProviderService.getEnvironmentBaseTitleForRuntime(appInitResult.EnvironmentInfo));
      });
  }

  loadingFeatures = false;
  reloadFeatures = false;

  initFlightSchoolFeatures() {
    try {

      this.loadFlightSchoolFeatures();

    } catch (e) {

    }
  }

  storeFlightSchoolFeatures(schoolFeatures: SchoolFeatures = this.schoolFeatures) {
    try {
      if (!schoolFeatures) {
        return;
      }

      sessionStorage.setItem('schoolFeatures', JSON.stringify(schoolFeatures));
    } catch (e) {

    }
  }

  loadFlightSchoolFeatures(forceSetTheme: boolean = false) {
    if (this.dwSecurityUserService.securityState.isAuthenticating || this.dwSecurityUserService.securityState.isRefreshing) {
      return;
    }

    if (this.schoolFeatures && this.currentUser?.FlightSchoolId != this.schoolFeatures?.FlightSchoolId) {
      if (this.loadingFeatures) {
        this.reloadFeatures = true;
        return;
      }

      this.reloadFeatures = false;
      this.loadingFeatures = true;
      this.schoolRepository.refreshSchoolFeatures()
        .subscribe({
          next: features => {
            this.setSchoolFeatures(features, forceSetTheme);
            this.loadingFeatures = false;
          },
          complete: () => this.loadingFeatures = false
        });
    }

    if (this.loadingFeatures) {
      return;
    }

    this.reloadFeatures = false;

    this.schoolRepository.getSchoolFeatures()
      .subscribe({
        next: features => {
          this.setSchoolFeatures(features);
          this.loadingFeatures = false;

          if (this.reloadFeatures) {
            this.loadFlightSchoolFeatures();
          }
        },
        complete: () => {
          this.loadingFeatures = false;
        }
      });
  }

  private setSchoolFeatures(schoolFeatures: SchoolFeatures, forceSetTheme: boolean = false) {
    // If nothings changed, don't do anything
    if (!forceSetTheme && areEqual(this.schoolFeatures, schoolFeatures)) {

      if (this.reloadFeatures) {
        this.loadingFeatures = false;
        this.loadFlightSchoolFeatures();
        return;
      }

      return;
    }
    if (!schoolFeatures) {
      console.warn('School features are null - not good!');
      return;
    }

    //console.log(`School Features loaded for School Id:${schoolFeatures.FlightSchoolId}  Theme Id: ${schoolFeatures.FlightSchoolThemeId}`);

    this.loadingFeatures = false;

    this.schoolFeatures = schoolFeatures;
    this.flightSchoolName = schoolFeatures.FlightSchoolName === 'Aviator Pro' ? 'AviatorPro' : schoolFeatures.FlightSchoolName;

    this.storeFlightSchoolFeatures(schoolFeatures);

    this.setTheme(this.menuService.menuState);

    if (this.reloadFeatures) {
      setTimeout(() => {
        this.loadingFeatures = false;
        this.loadFlightSchoolFeatures();
        return;
      });
    }
  }

  changeSchool() {
    if (this.adminManagerService.canChangeFlightSchool()) {
      this.schoolSelectModalActionService.handleAction(null).subscribe(() => {
        this.loadFlightSchoolFeatures();
      });
    }
  }

  private annonymousSetupStarted = false;
  oldUser: AviatorUser;

  sameSchoolRefreshedCount = 0;

  private setTheme(menuState: DwMenuServiceState) {

    this.currentUser = this.adminManagerService.getAviatorUser(this.dwSecurityUserService.securityState?.securityContext?.ApplicationUser);

    if (this.oldUser?.FlightSchoolId != this.currentUser?.FlightSchoolId || (this.currentUser?.FlightSchoolId && this.currentUser?.FlightSchoolId != this.schoolFeatures?.FlightSchoolId)) {
      this.oldUser = this.currentUser;

      if (this.schoolFeatures?.FlightSchoolId && this.currentUser?.FlightSchoolId) {
        // Only refresh the school x times in case it's returning the default school instead (This can happen if the school is inactive or other cases)
        this.sameSchoolRefreshedCount++;

        if (this.sameSchoolRefreshedCount < 3) {
          this.loadFlightSchoolFeatures();

          return;
        }
      }
    }

    this.oldUser = this.currentUser;

    this.flightSchoolOverridden = (this.currentUser.RealFlightSchoolId != this.currentUser.FlightSchoolId);

    this.schoolBrandingService.setupSchoolBranding(this.currentUser, this.schoolFeatures)
      .subscribe((applyingBrandingChange) => {
        if (!applyingBrandingChange) {
          this.finalSetTheme(menuState);

          if (!this.initialThemeComplete) {
            this.initialThemeComplete = true;
            //console.log('Hide splash scree');
          }

          this.showSplashScreen = false;
          //console.log('School branding setup complete', applyingBrandingChange);
        }
      });
  }

  private appSetupComplete = false;
  private updateThemes(...themes: string[]) {
    themes = themes.filter(t => !!t);

    if (this.appSetupComplete) {
      themes.push('app-setup-complete');
    }

    const newThemes = themes.join(',');

    if (this.pendingThemeClasses == newThemes && this.themeUpdateQueued) {
      return;
    }

    if (this.themeClasses == newThemes && !this.themeUpdateQueued) {
      return;
    }

    this.pendingThemeClasses = newThemes;

    if (this.themeUpdateQueued) {
      return;
    }

    this.themeUpdateQueued = true;

    Promise.resolve().then(() => {
      this.themeUpdateQueued = false;

      if (this.themeClasses != this.pendingThemeClasses) {
        this.themeClasses = this.pendingThemeClasses;
      }
    });
  }

  private finalSetTheme(menuState: DwMenuServiceState) {
    //console.log('Final Set Theme');
    const currentMenu = menuState.currentMenu;

    if (!this.appSetupComplete) {
      setTimeout(() => {

        this.appSetupComplete = true;
        this.updateThemes(this.themeClasses);
      }, 50);
    }

    this.showSideLogo = !(menuState?.currentMenu?.url?.indexOf('my-courses/') >= 0);

    const isAdminRoute = currentMenu?.url?.indexOf('admin') >= 0;
    const isHomeRoute = !currentMenu || currentMenu?.data?.referenceKey == 'home' || currentMenu?.url == '' || currentMenu?.url == '/';

    let themeClass = Themes.main;

    if (isAdminRoute) {
      themeClass = Themes.admin;
    } else if (isHomeRoute) {
      themeClass = Themes.home;
    }

    const themes: string[] = [themeClass];

    if (this.scrolled) {
      themes.push('scrolled');
    }

    this.courseSideBarClass = '';
    if (currentMenu?.url.indexOf('courses/view/') >= 0) {
      this.courseSideBarClass = 'course-side-bar-width';
    }

    if (!this.dwSecurityUserService.securityState.isAuthenticated) {

      if (this.dwSecurityUserService.securityState.authenticationType == DwAuthenticationType.logout && !this.logoutComplete) {

        setTimeout(() => {
          this.updateThemes(...themes, 'home-page-annonymous');
          this.logoutComplete = true;

        }, 250);
        return;
      } else {
        themes.push('home-page-annonymous');
      }
    } else {
      this.logoutComplete = false;
    }

    this.updateThemes(...themes);

    // console.log('theme Classes', this.themeClasses);

    this.deselectText();
  }

  private deselectText() {
    try {
      if (window.getSelection) {
        window.getSelection().removeAllRanges();
      }
      else
        if ((document as any).selection) {
          (document as any).selection.empty();
        }
    }
    catch (e) {

    }
  }

  ngOnDestroy(): void {
    document.removeEventListener("scroll", (ev) => { });
  }

  private shouldRedirectToLoginAfterInactivityLogout(state: any): boolean {
    if (!state || state.isAuthenticated) {
      return false;
    }

    if (state.authenticationType !== DwAuthenticationType.logout || !state.inactivityLogoutComplete) {
      return false;
    }

    const currentUrl = this.router.url || '';

    if (currentUrl === '/login' || currentUrl.startsWith('/login?') || currentUrl === '/register' || currentUrl.startsWith('/register?')) {
      return false;
    }

    return true;
  }

}


enum Themes {
  home = 'home-theme',
  main = 'main-theme',
  admin = 'admin-theme'
}
